import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Castle, Users, TrendingUp, TrendingDown, DollarSign,
  ChevronLeft, ChevronRight, Banknote, CreditCard, ArrowLeftRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function ReportesAdminPage() {
  const { getAuthHeader } = useAuth();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [periodo, setPeriodo] = useState('mes');
  const [resumen, setResumen] = useState(null);
  const [inflables, setInflables] = useState([]);
  const [clientesTop, setClientesTop] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = `mes=${selectedMonth}&anio=${selectedYear}&periodo=${periodo}`;
      const [resumenRes, inflablesRes, clientesRes] = await Promise.all([
        axios.get(`${API_URL}/finanzas/resumen?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/reportes/inflables?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/reportes/clientes?${params}`, { headers: getAuthHeader() }),
      ]);
      setResumen(resumenRes.data);
      setInflables(inflablesRes.data);
      setClientesTop(clientesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedMonth, selectedYear, periodo]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevMonth = () => { if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(selectedYear - 1); } else setSelectedMonth(selectedMonth - 1); };
  const nextMonth = () => { if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(selectedYear + 1); } else setSelectedMonth(selectedMonth + 1); };

  const periodoLabel = periodo === 'mes' ? `${meses[selectedMonth - 1]} ${selectedYear}` : periodo === 'anio' ? `${selectedYear}` : 'Todo el tiempo';

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="reportes-admin-page">
      <div>
        <h1 className="font-heading text-xl sm:text-3xl font-bold text-allegra-dark">Reportes</h1>
        <p className="text-xs sm:text-sm text-allegra-gray">Analisis de tu negocio</p>
      </div>

      {/* Period Tabs */}
      <div className="flex flex-col gap-3">
        <div className="flex rounded-xl bg-muted p-1 gap-1" data-testid="reportes-periodo-tabs">
          {[{v:'mes',l:'Mes'},{v:'anio',l:'Ano'},{v:'global',l:'Global'}].map(t => (
            <button key={t.v} onClick={() => setPeriodo(t.v)}
              className={`flex-1 text-xs sm:text-sm font-medium py-2 rounded-lg transition-all ${periodo === t.v ? 'bg-white shadow text-allegra-dark' : 'text-allegra-gray hover:text-allegra-dark'}`}
              data-testid={`reportes-tab-${t.v}`}>
              {t.l}
            </button>
          ))}
        </div>
        {periodo === 'mes' && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium text-allegra-dark min-w-[140px] text-center">{meses[selectedMonth - 1]} {selectedYear}</span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
        {periodo === 'anio' && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedYear(selectedYear - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium text-allegra-dark">{selectedYear}</span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedYear(selectedYear + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div></div>
      ) : (
        <>
          {/* Financial Summary */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
            <Card className="border-0 shadow-md">
              <CardContent className="p-3 sm:p-5 text-center">
                <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="font-heading text-lg sm:text-2xl font-bold text-green-600 truncate">${resumen?.total_ingresos?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-allegra-gray">Ingresos</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-3 sm:p-5 text-center">
                <TrendingDown className="w-5 h-5 text-red-500 mx-auto mb-1" />
                <p className="font-heading text-lg sm:text-2xl font-bold text-red-500 truncate">${resumen?.total_egresos?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-allegra-gray">Egresos</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-3 sm:p-5 text-center">
                <DollarSign className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className={`font-heading text-lg sm:text-2xl font-bold truncate ${(resumen?.balance || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>${resumen?.balance?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-allegra-gray">Balance</p>
              </CardContent>
            </Card>
          </div>

          {/* Payment method breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-medium text-green-600">Ingresos por forma de pago</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  {[{k:'efectivo',icon:Banknote,l:'Efectivo'},{k:'transferencia',icon:ArrowLeftRight,l:'Transferencia'},{k:'tarjeta',icon:CreditCard,l:'Tarjeta'}].map(({k,icon:Icon,l}) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-allegra-gray" /><span>{l}</span></div>
                      <span className="font-bold text-green-600">${(resumen?.ingresos_por_forma_pago?.[k] || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-medium text-red-500">Egresos por forma de pago</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  {[{k:'efectivo',icon:Banknote,l:'Efectivo'},{k:'transferencia',icon:ArrowLeftRight,l:'Transferencia'},{k:'tarjeta',icon:CreditCard,l:'Tarjeta'}].map(({k,icon:Icon,l}) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-allegra-gray" /><span>{l}</span></div>
                      <span className="font-bold text-red-500">${(resumen?.egresos_por_forma_pago?.[k] || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Inflables & Clientes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            <Card className="border-0 shadow-lg" data-testid="reporte-inflables">
              <CardHeader className="pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
                <CardTitle className="text-sm sm:text-lg font-heading flex items-center gap-2">
                  <Castle className="w-4 h-4 text-primary" />
                  Inflables Mas Rentados - {periodoLabel}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6 pb-4 sm:pb-6">
                {inflables.length === 0 ? (
                  <p className="text-allegra-gray text-sm text-center py-6">Sin datos en este periodo</p>
                ) : (
                  <div className="space-y-2">
                    {inflables.map((inf, idx) => (
                      <div key={inf.inflable_id} className="flex items-center justify-between p-2.5 bg-muted/50 rounded-xl gap-2" data-testid={`reporte-inflable-${inf.inflable_id}`}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${idx === 0 ? 'bg-primary' : idx === 1 ? 'bg-accent' : 'bg-allegra-gray'}`}>
                            {idx + 1}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-allegra-dark truncate">{inf.nombre}</p>
                            <p className="text-xs text-allegra-gray">{inf.total_rentas} renta(s)</p>
                          </div>
                        </div>
                        <span className="font-bold text-sm text-green-600 whitespace-nowrap">${inf.ingresos?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg" data-testid="reporte-clientes">
              <CardHeader className="pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
                <CardTitle className="text-sm sm:text-lg font-heading flex items-center gap-2">
                  <Users className="w-4 h-4 text-accent" />
                  Clientes Frecuentes - {periodoLabel}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6 pb-4 sm:pb-6">
                {clientesTop.length === 0 ? (
                  <p className="text-allegra-gray text-sm text-center py-6">Sin datos en este periodo</p>
                ) : (
                  <div className="space-y-2">
                    {clientesTop.map((cli, idx) => (
                      <div key={cli.cliente_id} className="flex items-center justify-between p-2.5 bg-muted/50 rounded-xl gap-2" data-testid={`reporte-cliente-${cli.cliente_id}`}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${idx === 0 ? 'bg-accent' : idx === 1 ? 'bg-primary' : 'bg-allegra-gray'}`}>
                            {cli.nombre?.charAt(0) || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-allegra-dark truncate">{cli.nombre}</p>
                            <p className="text-xs text-allegra-gray">{cli.total_rentas} renta(s)</p>
                          </div>
                        </div>
                        <span className="font-bold text-sm text-allegra-dark whitespace-nowrap">${cli.gasto_total?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
